/* eslint-disable @typescript-eslint/no-unsafe-call */
import { Button } from 'antd'
import { Fragment, useEffect, useRef, useState } from 'react'
import ReactDOM, { createPortal } from 'react-dom'

import { type Question } from '@/types'
import Tooltip from '@/ui/atoms/Tooltip'
import cn from '@/utils/tailwind/cn'

import { CalculationKeyboard } from '../CalculationKeyboard'
import { COMPARISON_OPERATORS, NUMBERS, OPERATORS } from '../constants'
import {
  generateId,
  isValidFunctionFormula,
  parseFunctionFormulaFromEditor,
} from '../utils'
import { generateHTMLFromFormula } from '../utils/generateHTMLFromFormula'
import { getAgeDetailValues } from '../utils/getAgeDetailValues'
import { DateQuestionDropdown } from './components/FieldsDropdown/DateQuestionDropdown'
import { QuestionAndMatrixDropdown } from './components/FieldsDropdown/QuestionAndMatrixDropdown'
import { FunctionDropdown } from './components/FunctionDropdown'
import { FunctionOptions } from './components/FunctionOptions'
import { getSpanFieldValue } from './components/getSpanFieldValue'
import {
  HtmlAgeDetailFunction,
  HtmlCountIfsFunction,
} from './components/HTMLFields'
import { HtmlIfFunction } from './components/HTMLFields/HtmlIfFunction'

const ALL_OPERATORS = [...NUMBERS, ...OPERATORS, ...COMPARISON_OPERATORS, '.']

export type DropdownInfoModel = { dataType: string; currentFunction?: string }
export type HandleSelectQuestionProps = {
  questionId?: number
  rowOrder?: number
  formIndex?: number
}

type Props = {
  questions: Question[]
  initialValues?: string
  onClose: () => void
  onSave: (formula: string) => void
}

export const CalculationFunction = ({
  questions,
  initialValues,
  onClose,
  onSave,
}: Props) => {
  const [tooltipLabel, setTooltipLabel] = useState<string | undefined>(
    undefined
  )
  const [dropdownInfo, setDropdownInfo] = useState<
    DropdownInfoModel | undefined
  >(undefined)
  const editorRef = useRef<HTMLDivElement | null>(null)
  const tooltipRef = useRef<HTMLDivElement | null>(null)
  const tooltipTimeoutId = useRef<NodeJS.Timeout | null>(null)
  const dropdownFunctionRef = useRef<HTMLDivElement | null>(null)
  const dropdownAddFieldRef = useRef<HTMLDivElement | null>(null)
  const dropdownAddDateFieldRef = useRef<HTMLDivElement | null>(null)
  const dropdownTimeoutId = useRef<NodeJS.Timeout | null>(null)
  const badgeRef = useRef<HTMLElement | null>(null)

  let buttonTitle = 'Add'
  if (initialValues) buttonTitle = 'Save'

  const [showFunctionType, setShowFunctionType] = useState<string | undefined>(
    undefined
  )

  const handleClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement

    if (target.tagName === 'SPAN') {
      const rect = target.getBoundingClientRect()
      const dataType = target.getAttribute('data-type')
      const dataFunction = target.getAttribute('data-function') || ''

      if (dataType) {
        const dropdownAddField = dropdownAddFieldRef.current
        const dropdownAddDateField = dropdownAddDateFieldRef.current
        const dropdownFunction = dropdownFunctionRef.current

        if (
          [
            'value',
            'addField',
            'addDateField',
            'condition',
            'whenTrue',
            'whenFalse',
            'function',
          ].includes(dataType)
        ) {
          badgeRef.current = target
        }

        if (dropdownFunction) {
          dropdownFunction.style.top = `${rect.top + 38}px`
          dropdownFunction.style.left = `${rect.left}px`
        }

        if (dropdownAddField) {
          dropdownAddField.style.top = `${rect.top + 40}px`
          dropdownAddField.style.left = `${rect.left}px`
        }

        if (dropdownAddDateField) {
          dropdownAddDateField.style.top = `${rect.top + 40}px`
          dropdownAddDateField.style.left = `${rect.left}px`
        }

        if (['function', 'addField', 'addDateField'].includes(dataType)) {
          if (dropdownTimeoutId.current) {
            clearTimeout(dropdownTimeoutId.current)
          }
          dropdownTimeoutId.current = setTimeout(() => {
            setDropdownInfo({
              dataType,
              currentFunction: dataFunction,
            })
          }, 100)
        }
      }
    }
  }

  const setCursorAfterElement = (element: HTMLElement) => {
    const range = document.createRange()
    const selection = window.getSelection()

    range.setStartAfter(element)
    range.setEndAfter(element)

    selection?.removeAllRanges()
    selection?.addRange(range)
  }

  const insertAtCursor = (text: string) => {
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) return

    const range = selection.getRangeAt(0)

    const container = range.startContainer
    const parentElement =
      container.nodeType === Node.TEXT_NODE
        ? container.parentElement
        : (container as HTMLElement)

    if (
      !parentElement ||
      !['value', 'condition', 'whenTrue', 'whenFalse'].includes(
        parentElement.getAttribute('data-type') || ''
      )
    ) {
      return
    }

    range.deleteContents()

    const textNode = document.createTextNode(text)
    range.insertNode(textNode)

    range.setStartAfter(textNode)
    range.setEndAfter(textNode)
    selection.removeAllRanges()
    selection.addRange(range)
  }

  const insertComponentAtCursor = (component: React.ReactNode) => {
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) return

    const range = selection.getRangeAt(0)
    if (range.startOffset === 0 && range.startContainer.previousSibling) {
      range.startContainer.previousSibling.remove()
    } else if (range.startOffset > 0) {
      const textNode = range.startContainer as Text
      textNode.deleteData(range.startOffset - 1, 1)
    }
    range.deleteContents()

    const container = document.createElement('span')
    container.className = 'nested-function'
    container.contentEditable = 'false'
    range.insertNode(container)

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    ReactDOM.render(component, container)

    range.setStartAfter(container)
    range.setEndAfter(container)
    selection.removeAllRanges()
    selection.addRange(range)
    setDropdownInfo(undefined)
  }

  const handleSelectQuestion = (
    title: string,
    value: string | number,
    props?: HandleSelectQuestionProps
  ) => {
    const badge = badgeRef.current
    const editor = editorRef.current

    if (!badge || !editor) return
    setDropdownInfo(undefined)

    const currentOrder = badge.getAttribute('data-order') || ''
    const element = editor.querySelector(`[data-order="${currentOrder}"]`)

    if (element) {
      const dataType = element.getAttribute('data-type') || ''

      if (['value', 'condition', 'whenTrue', 'whenFalse'].includes(dataType)) {
        const fieldId = generateId()
        const text = element.innerHTML || ''
        const newTag = text.replace(
          /\b[qQ]\b/g,
          getSpanFieldValue(title, value, fieldId)
        )
        element.innerHTML = newTag

        const fieldElement = element.querySelector(
          `span[data-id="${fieldId}"]`
        ) as HTMLElement
        if (fieldElement) {
          setCursorAfterElement(fieldElement)
        }

        return
      }

      element.setAttribute('data-description', title)

      if (props?.questionId !== undefined) {
        element.setAttribute('data-question-id', String(props.questionId))
      }
      if (props?.rowOrder !== undefined) {
        element.setAttribute('data-row-order', String(props.rowOrder))
      }
      if (props?.formIndex !== undefined) {
        element.setAttribute('data-form-index', String(props.formIndex))
      }

      element.setAttribute('data-value', String(value))
      element.setAttribute('data-select', 'true')
      element.textContent = title
    }
  }

  const checkPreviousChar = (ev: KeyboardEvent) => {
    const selection = window.getSelection()
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0)
      const startContainer = range.startContainer
      const startOffset = range.startOffset

      if (startContainer.nodeType === Node.TEXT_NODE) {
        const text = startContainer.textContent || ''
        const prevChar = text.charAt(startOffset - 1)
        return prevChar.toLowerCase() === ev.key.toLowerCase()
      }
    }
    return false
  }

  const keyDownValueEvent = (ev: KeyboardEvent) => {
    const isNumber = /^[0-9.]$/.test(ev.key)
    const isOperator = /^[+\-*/]$/.test(ev.key)
    const isParentheses = /^[()]$/.test(ev.key)
    const isWords = /[qQfF]/.test(ev.key)
    const isComparisonOperators = /[>=<]/.test(ev.key)

    const isAllowedKey = [
      'Backspace',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      ' ',
    ].includes(ev.key)

    if (
      !isNumber &&
      !isOperator &&
      !isAllowedKey &&
      !isParentheses &&
      !isWords &&
      !isComparisonOperators
    ) {
      ev.preventDefault()
      return
    }

    if (['q', 'Q'].includes(ev.key)) {
      if (checkPreviousChar(ev)) {
        ev.preventDefault()
        return
      }

      setDropdownInfo({ dataType: 'field' })
    } else if (['f', 'F'].includes(ev.key)) {
      if (checkPreviousChar(ev)) {
        ev.preventDefault()
        return
      }

      setDropdownInfo({ dataType: 'insertHTMLFunction' })
    } else {
      setDropdownInfo(undefined)
    }
  }

  const mouseOverEvent = (e: MouseEvent) => {
    const target = e.target as HTMLElement
    const tooltip = tooltipRef.current

    if (!tooltip || !target) return

    if (tooltipTimeoutId.current) {
      clearTimeout(tooltipTimeoutId.current)
    }

    const dataType = target.getAttribute('data-type') || ''

    if (dataType && ['addField', 'function'].includes(dataType)) {
      const rect = target.getBoundingClientRect()

      tooltip.style.display = 'block'
      tooltip.style.zIndex = '9999'
      tooltip.style.position = 'absolute'
      tooltip.style.top = `${rect.top}px`
      tooltip.style.left = `${rect.left}px`
      tooltip.style.width = `${rect.width}px`
      tooltip.style.height = '1px'
      tooltipTimeoutId.current = setTimeout(() => {
        setTooltipLabel(target.getAttribute('data-description') || '')
      }, 700)
      return
    }
    setTooltipLabel(undefined)
  }

  const onSaveFunction = () => {
    const parsedFormula = parseFunctionFormulaFromEditor(editorRef)

    if (isValidFunctionFormula(parsedFormula)) {
      onSave(parsedFormula)
    }
  }

  const editorAddEventListeners = () => {
    const editor = editorRef.current
    if (!editor) return

    editor.addEventListener('mouseover', mouseOverEvent)
    editor.addEventListener('keydown', keyDownValueEvent)
  }

  const removeEditorEventListeners = () => {
    const editor = editorRef.current
    if (!editor) return

    editor.removeEventListener('mouseover', mouseOverEvent)
    editor.removeEventListener('keydown', keyDownValueEvent)
  }

  useEffect(() => {
    document.addEventListener('click', handleClick)
    editorAddEventListeners()

    const editor = editorRef.current
    if (!editor) return

    if (initialValues) {
      if (initialValues.toLowerCase().includes('agedetail')) {
        const ageDetail = getAgeDetailValues(initialValues, questions)
        ReactDOM.render(<HtmlAgeDetailFunction {...ageDetail} />, editor)
        return
      }
      editor.innerHTML = generateHTMLFromFormula(initialValues, questions)
    }

    return () => {
      document.removeEventListener('click', handleClick)
      removeEditorEventListeners()
      setTooltipLabel(undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <div className="mt-4 flex flex-col rounded-md border border-border">
        <CalculationKeyboard.Root className="grid-cols-11 grid-rows-1 gap-0.5">
          {ALL_OPERATORS.map((item, index) => {
            const keyboardButtonStyle = {
              '!rounded-tl-md': index === 0,
              '!rounded-tr-md': index === 10,
            }
            return (
              <CalculationKeyboard.Button
                className={cn(keyboardButtonStyle, 'min-h-10 !px-2')}
                key={item}
                onClick={() => {
                  insertAtCursor(item)
                  setDropdownInfo(undefined)
                }}
              >
                {item}
              </CalculationKeyboard.Button>
            )
          })}
          <FunctionOptions
            onClick={(key) => {
              if (editorRef.current && !showFunctionType) {
                editorRef.current.innerHTML = ''
              }
              setShowFunctionType(key)
            }}
          />
        </CalculationKeyboard.Root>
        <div className="relative min-h-32 w-full">
          <div
            className={cn(
              'relative left-0 top-0 z-[2] h-full w-full px-2 pb-3 pt-2.5 outline-blue-200 focus:!border-blue-200',
              'active:!border-blue-200',
              'empty:before:content-["Select_a_function_to_initiate"]',
              'empty:before:text-gray-300',

              '[&_span[data-type="function"]]:inline-block [&_span[data-type="function"]]:font-bold [&_span[data-type="function"]]:text-primary',
              '[&_span[data-type="function"]]:cursor-default [&_span[data-type="function"]]:select-none',
              '[&_span[data-type="function"]]:mt-2.5 [&_span[data-type="function"]]:rounded [&_span[data-type="function"]]:p-1',

              '[&_span[data-type="parentheses"]]:mx-1 [&_span[data-type="parentheses"]]:text-primary',
              '[&_span[data-type="parentheses"]]:font-bold',

              '[&_span[data-type="addField"]]:border [&_span[data-type="addField"]]:border-gray-300',
              '[&_span[data-type="addField"]]:rounded [&_span[data-type="addField"]]:p-1.5',
              '[&_span[data-type="addField"]]:cursor-pointer [&_span[data-type="addField"]]:text-gray-500',
              '[&_span[data-type="addField"]:hover]:bg-gray-100 [&_span[data-type="addField"]]:min-w-[99px] [&_span[data-type="addField"]]:text-center',
              '[&_span[data-type="addField"]]:inline-block',

              '[&_span[data-type="addDateField"]]:border [&_span[data-type="addDateField"]]:border-gray-300',
              '[&_span[data-type="addDateField"]]:rounded [&_span[data-type="addDateField"]]:p-1.5',
              '[&_span[data-type="addDateField"]]:cursor-pointer [&_span[data-type="addDateField"]]:text-gray-500',
              '[&_span[data-type="addDateField"]:hover]:bg-gray-100 [&_span[data-type="addDateField"]]:min-w-[99px] [&_span[data-type="addDateField"]]:text-center',
              '[&_span[data-type="addDateField"]]:inline-block',

              '[&_span[data-select="true"]]:!border-gray-400 [&_span[data-select="true"]]:font-medium [&_span[data-select="true"]]:!text-gray-600',

              '[&_span[data-type="wrapperPlaceholder"]]:inline-block [&_span[data-type="wrapperPlaceholder"]]:rounded',
              '[&_span[data-type="wrapperPlaceholder"]]:relative [&_span[data-type="wrapperPlaceholder"]]:h-[36px] [&_span[data-type="wrapperPlaceholder"]]:min-w-[106px]',
              '[&_span[data-type="wrapperPlaceholder"]]:font-medium [&_span[data-type="wrapperPlaceholder"]]:leading-[18px]',
              '[&_span[data-type="wrapperPlaceholder"]]:mt-2.5',

              '[&_span[data-type="placeholder"]]:absolute [&_span[data-type="placeholder"]]:text-gray-300',
              '[&_span[data-type="placeholder"]]:rounded [&_span[data-type="placeholder"]]:px-1.5',
              '[&_span[data-type="placeholder"]]:-top-[1px] [&_span[data-type="placeholder"]]:h-full [&_span[data-type="placeholder"]]:w-full',
              '[&_span[data-type="placeholder"]]:left-0 [&_span[data-type="placeholder"]]:text-nowrap',
              '[&_span[data-type="placeholder"]]:z-[1] [&_span[data-type="placeholder"]]:leading-[35px]',

              '[&_span[data-mask="input"]]:border [&_span[data-mask="input"]]:border-gray-300',
              '[&_span[data-mask="input"]]:rounded [&_span[data-mask="input"]]:px-1 [&_span[data-mask="input"]]:pt-2',
              '[&_span[data-mask="input"]]:text-gray-500',
              '[&_span[data-mask="input"]]:left-0 [&_span[data-mask="input"]]:top-0 [&_span[data-mask="input"]]:z-[2]',
              '[&_span[data-mask="input"]]:relative [&_span[data-mask="input"]]:block',
              '[&_span[data-mask="input"]]:h-full [&_span[data-mask="input"]]:min-w-[106px] [&_span[data-mask="input"]]:text-nowrap',
              '[&_span[data-mask="input"]]:-mt-[1px] [&_span[data-mask="input"]]:rounded',
              '[&_span[data-mask="input"]:focus-visible]:outline-gray-400',

              '[&_span[data-type="rangeSeparator"]]:mx-1 [&_span[data-type="rangeSeparator"]]:font-bold',

              '[&_span[data-type="semicolon"]]:mx-1 [&_span[data-type="semicolon"]]:font-bold',

              '[&_span[data-type="comma"]]:mx-1 [&_span[data-type="comma"]]:font-bold',

              '[&_span[data-type="addedField"]]:border [&_span[data-type="addedField"]]:border-primary/80 [&_span[data-type="addedField"]]:bg-primary/80',
              '[&_span[data-type="addedField"]]:rounded [&_span[data-type="addedField"]]:font-medium [&_span[data-type="addedField"]]:text-white',
              '[&_span[data-type="addedField"]]:px-1.5 [&_span[data-type="addedField"]]:py-0.5',

              '[&_span[data-type="value"]]:relative [&_span[data-type="value"]]:block [&_span[data-type="value"]]:text-primary/70',
              '[&_span[data-type="value"]]:-mt-[1px] [&_span[data-type="value"]]:rounded [&_span[data-type="value"]]:px-1',
              '[&_span[data-type="value"]]:left-0 [&_span[data-type="value"]]:top-0 [&_span[data-type="value"]]:z-[2]',
              '[&_span[data-type="value"]]:h-full [&_span[data-type="value"]]:min-w-[48px] [&_span[data-type="value"]]:leading-[18px]',
              '[&_span[data-type="value"]]:text-nowrap [&_span[data-type="value"]]:border [&_span[data-type="value"]]:border-primary/40',
              '[&_span[data-type="value"]:focus-visible]:outline-primary',

              '[&_span[data-color="blue"]]:!text-primary/70 [&_span[data-placeholder="countifs"]]:!min-w-[48px]',

              '[&_span[data-color="green"]]:!text-green-400 [&_span[data-type="whenTrue"]]:min-w-[117px] [&_span[data-type="whenTrue"]]:text-green-500/70',
              '[&_span[data-type="whenTrue"]:focus-visible]:outline-green-400 [&_span[data-type="whenTrue"]]:border-green-500/40',

              '[&_span[data-color="red"]]:!text-red-400 [&_span[data-type="whenFalse"]]:min-w-[122px] [&_span[data-type="whenFalse"]]:text-red-500/70',
              '[&_span[data-type="whenFalse"]:focus-visible]:outline-red-400 [&_span[data-type="whenFalse"]]:border-red-500/40',

              '[&_span.nested-function>span[data-type="function"]:hover]:bg-white [&_span.nested-function]:-mt-4 [&_span.nested-function]:inline-block',
              '[&_span.nested-function>span[data-type="function"]]:!mt-0 [&_span.nested-function>span[data-type="wrapperPlaceholder"]]:!mt-0',
              '[&_span.nested-function>span[data-type="wrapperPlaceholder"]]:!top-0 [&_span.nested-function>span[data-type="wrapperPlaceholder"]]:!h-[23px]',
              '[&_span.nested-function>span>span[data-type="placeholder"]]:!leading-6 [&_span.nested-function>span>span[data-type="value"]]:!leading-[4px]',
              '[&_span.nested-function>span>span[data-type="value"]>span[data-type="addedField"]]:!py-0 [&_span.nested-function>span>span[data-type="value"]>span[data-type="addedField"]]:px-1',
              '[&_span.nested-function>span[data-type="addField"]]:!py-0.5'
            )}
            ref={editorRef}
          >
            {showFunctionType === 'countifs' ? <HtmlCountIfsFunction /> : null}
            {showFunctionType === 'if' ? <HtmlIfFunction /> : null}
            {showFunctionType === 'agedetail' ? (
              <HtmlAgeDetailFunction />
            ) : null}
          </div>
        </div>
      </div>
      <div className="mt-3 flex justify-end gap-4">
        <div className="flex w-full flex-col gap-1">
          {[
            '*Type Q to open the select field',
            '*Type F to open the select function',
          ].map((item) => (
            <span className="text-xs font-medium text-gray-500" key={item}>
              {item}
            </span>
          ))}
        </div>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onSaveFunction} type="primary">
          {buttonTitle}
        </Button>
      </div>
      {createPortal(
        <Fragment>
          <div ref={tooltipRef}>
            <Tooltip label={tooltipLabel} open={Boolean(tooltipLabel)}>
              <span className="block h-[1px] w-full" />
            </Tooltip>
          </div>
          <FunctionDropdown
            onClose={() => setDropdownInfo(undefined)}
            onMouseOver={() => setTooltipLabel(undefined)}
            onSelectItem={() =>
              insertComponentAtCursor(<HtmlCountIfsFunction />)
            }
            ref={dropdownFunctionRef}
            {...{ dropdownInfo }}
          />
          <QuestionAndMatrixDropdown
            onClose={() => setDropdownInfo(undefined)}
            onMouseOver={() => setTooltipLabel(undefined)}
            onSelectQuestion={handleSelectQuestion}
            ref={dropdownAddFieldRef}
            {...{ questions, dropdownInfo }}
          />
          <DateQuestionDropdown
            onClose={() => setDropdownInfo(undefined)}
            onMouseOver={() => setTooltipLabel(undefined)}
            onSelectQuestion={handleSelectQuestion}
            ref={dropdownAddDateFieldRef}
            {...{ questions, dropdownInfo }}
          />
        </Fragment>,
        document.body
      )}
    </>
  )
}
